import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import FormSelect from "../common/FormSelect";
import {
  useGetAllCategoriesQuery,
  useGetSingleCategoryAdminQuery,
} from "../../services/categories/categoriesApi";

const getOptionFromValue = (value) => {
  if (value && typeof value === "object") {
    const label =
      value.title || value.name || value.label || value.categoryName || "";
    const optionValue = value._id || value.value || label;

    return label ? { label, value: optionValue } : null;
  }

  if (value) {
    return { label: String(value), value };
  }

  return null;
};

const BasicInformationSection = ({ courseData, onChange }) => {
  const [title, setTitle] = useState("");
  const [liveClassPrice, setLiveClassPrice] = useState("");
  const [selectedCategoryValue, setSelectedCategoryValue] = useState("");
  const [selectedSubCategoryValue, setSelectedSubCategoryValue] = useState("");
  const [selectedAgeGroupValue, setSelectedAgeGroupValue] = useState("");
  const [selectedLevelValue, setSelectedLevelValue] = useState("");
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const thumbnailInputRef = useRef(null);

  const { data: categoriesResponse, isLoading: isCategoriesLoading } =
    useGetAllCategoriesQuery({ page: 1, limit: 100 });

  const { data: singleCategoryResponse, isFetching: isCategoryDetailsLoading } =
    useGetSingleCategoryAdminQuery(selectedCategoryValue, {
      skip: !selectedCategoryValue,
    });

  const selectedCategoryDetails = selectedCategoryValue
    ? singleCategoryResponse?.data || null
    : null;

  const categoryOptions = useMemo(() => {
    if (!Array.isArray(categoriesResponse?.data)) {
      return [];
    }

    return categoriesResponse.data.map((category) => ({
      label: category.categoryName || "-",
      value: category._id || "",
    }));
  }, [categoriesResponse]);

  useEffect(() => {
    const courseCategoryName = courseData?.courseCategory?.categoryName;

    if (!courseCategoryName || !Array.isArray(categoriesResponse?.data)) {
      return;
    }

    const matchedCategory = categoriesResponse.data.find(
      (category) =>
        String(category.categoryName).toLowerCase() ===
        String(courseCategoryName).toLowerCase(),
    );

    if (matchedCategory) {
      setSelectedCategoryValue(matchedCategory._id);
    }
  }, [courseData?.courseCategory, categoriesResponse]);

  useEffect(() => {
    setTitle(courseData?.courseTitle || "");
    setLiveClassPrice(
      courseData?.coursePrice !== undefined && courseData?.coursePrice !== null
        ? String(courseData.coursePrice)
        : "",
    );
    setSelectedSubCategoryValue(
      getOptionFromValue(courseData?.courseSubCategory)?.value || "",
    );
    setSelectedAgeGroupValue(
      getOptionFromValue(courseData?.courseAgeGroup)?.value || "",
    );
    setSelectedLevelValue(
      getOptionFromValue(courseData?.courseLevel)?.value || "",
    );
    setThumbnailPreview(courseData?.courseThumbnail?.secureUrl || "");
  }, [courseData?._id]);

  useEffect(() => {
    return () => {
      if (thumbnailPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(thumbnailPreview);
      }
    };
  }, [thumbnailPreview]);

  const handleThumbnailChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setThumbnailPreview(previewUrl);

    if (typeof onChange === "function") {
      onChange({ courseThumbnail: file });
    }
  };

  const subCategoryOptions = useMemo(() => {
    const fromCategory = (selectedCategoryDetails?.subCategory || [])
      .map((item) => ({
        label: item?.title || item?.name || "-",
        value: item?._id || item?.value || "",
      }))
      .filter((option) => option.value);

    const defaultOption = getOptionFromValue(courseData?.courseSubCategory);

    if (
      defaultOption &&
      !fromCategory.some((option) => option.value === defaultOption.value)
    ) {
      return [defaultOption, ...fromCategory];
    }

    return fromCategory;
  }, [selectedCategoryDetails, courseData?.courseSubCategory]);

  const ageGroupOptions = useMemo(() => {
    const fromCategory = (selectedCategoryDetails?.categoryAgeGroup || [])
      .map((item) => ({
        label: item?.title || item?.name || "-",
        value: item?._id || item?.value || "",
      }))
      .filter((option) => option.value);

    const defaultOption = getOptionFromValue(courseData?.courseAgeGroup);

    if (
      defaultOption &&
      !fromCategory.some((option) => option.value === defaultOption.value)
    ) {
      return [defaultOption, ...fromCategory];
    }

    return fromCategory;
  }, [selectedCategoryDetails, courseData?.courseAgeGroup]);

  const levelOptions = useMemo(() => {
    const fromCategory = (selectedCategoryDetails?.categoryLevel || [])
      .map((item) => ({
        label: item?.title || item?.name || "-",
        value: item?._id || item?.value || "",
      }))
      .filter((option) => option.value);

    const defaultOption = getOptionFromValue(courseData?.courseLevel);

    if (
      defaultOption &&
      !fromCategory.some((option) => option.value === defaultOption.value)
    ) {
      return [defaultOption, ...fromCategory];
    }

    return fromCategory;
  }, [selectedCategoryDetails, courseData?.courseLevel]);

  const basicInfoFields = [
    title?.trim(),
    selectedCategoryValue,
    selectedSubCategoryValue,
    courseData?.courseEnrollementType,
    selectedAgeGroupValue,
    selectedLevelValue,
    liveClassPrice,
  ];

  const completedFieldsCount = basicInfoFields.filter(
    (value) => String(value || "").trim().length > 0,
  ).length;
  const totalFieldsCount = basicInfoFields.length;
  const isBasicInfoComplete = completedFieldsCount === totalFieldsCount;

  if (!courseData) return null;

  return (
    <section className="mt-6   overflow-hidden rounded-xl border    border-[#E5E7EB]  bg-white shadow-[0_1px_2px_#0000000D] ">
      <header className="flex flex-col gap-3 border-b border-[#E5E7EB]  bg-[#F9FAFB] px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-base text-[#1F2937] sm:text-lg">
            Basic Information
          </h3>
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs ${
              isBasicInfoComplete
                ? "bg-[#DCFCE7] text-[#166534]"
                : "bg-[#FEE2E2] text-[#991B1B]"
            }`}
          >
            {isBasicInfoComplete ? "Complete" : "Incomplete"}
          </span>
        </div>

        <p className="text-sm text-[#6B7280]">
          {completedFieldsCount}/{totalFieldsCount} fields completed
        </p>
      </header>
      <div className="  grid grid-cols-1 gap-6 px-4 pb-8 pt-6 sm:px-8 sm:gap-10 md:px-11 md:pb-10 md:pt-9 lg:grid-cols-2 lg:gap-[90px]">
        <div className="min-w-0">
          <div>
            <label
              htmlFor="course-title"
              className="mb-1 block text-sm  text-[#374151]"
            >
              Add Title <span className="text-[#EF4444]">*</span>
            </label>
            <input
              id="course-title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (typeof onChange === "function") {
                  onChange({ courseTitle: e.target.value });
                }
              }}
              placeholder="title"
              className="h-[42px] w-full rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm text-[#000] outline-none sm:text-base"
            />
          </div>

          <div className="mt-4 sm:mt-8">
            <p className="mb-1 text-sm flex items-center gap-3  text-[#374151]">
              Course Thumbnail <Check size={14} className="text-[#16A34A]" />
            </p>

            <div className="rounded-lg border-2 border-dashed border-[#D1D5DB] bg-white px-4 pb-5 pt-5 sm:px-5 sm:pb-[26px] sm:pt-[22px]">
              <div className="mx-auto w-full max-w-[255px]">
                <img
                  src={thumbnailPreview}
                  alt="Course thumbnail"
                  className="h-36 w-full rounded-lg object-cover sm:h-[160px]"
                />
              </div>

              <input
                ref={thumbnailInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleThumbnailChange}
              />

              <div className="mt-1 flex justify-center">
                <button
                  type="button"
                  onClick={() => thumbnailInputRef.current?.click()}
                  className="rounded border border-[#D1D5DB] bg-white px-[13px] py-[7px] text-sm text-[#374151]"
                >
                  Change Image
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <FormSelect
            label="Category"
            isRequired={true}
            placeholder={
              isCategoriesLoading ? "Loading categories..." : "Select category"
            }
            value={selectedCategoryValue}
            options={categoryOptions}
            labelClassName="mb-1 text-sm text-[#374151]"
            optionClassName="capitalize"
            controlWrapperClassName="mt-0"
            selectClassName="h-[42px] capitalize rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm text-[#000] sm:text-base"
            icon={ChevronDown}
            iconProps={{ size: 20, className: "text-[#000]" }}
            dropdownClassName="max-h-[45vh] w-full overflow-y-auto rounded-lg"
            disabled={isCategoriesLoading}
            onChange={(value) => {
              setSelectedCategoryValue(value);
              setSelectedSubCategoryValue("");
              setSelectedAgeGroupValue("");
              setSelectedLevelValue("");

              if (typeof onChange === "function") {
                onChange({
                  courseCategory: value,
                  courseSubCategory: "",
                  courseAgeGroup: "",
                  courseLevel: "",
                });
              }
            }}
          />

          <div className="mt-5 sm:mt-7">
            <FormSelect
              label="Select Sub Category"
              placeholder={
                isCategoryDetailsLoading
                  ? "Loading..."
                  : selectedCategoryValue
                    ? "Select sub category"
                    : "Select category first"
              }
              options={subCategoryOptions}
              value={selectedSubCategoryValue}
              labelClassName="mb-1 text-sm text-[#374151]"
              controlWrapperClassName="mt-0"
              optionClassName="capitalize"
              selectClassName="h-[42px] capitalize rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm text-[#000] sm:text-base"
              icon={ChevronDown}
              iconProps={{ size: 20, className: "text-[#000]" }}
              dropdownClassName="max-h-[45vh] w-full overflow-y-auto rounded-lg"
              disabled={!selectedCategoryValue}
              onChange={(value) => {
                setSelectedSubCategoryValue(value);
                if (typeof onChange === "function") {
                  onChange({ courseSubCategory: value });
                }
              }}
            />
          </div>

          <div className="mt-5 sm:mt-7">
            <FormSelect
              label="Level"
              placeholder={
                isCategoryDetailsLoading
                  ? "Loading..."
                  : selectedCategoryValue
                    ? "Select level"
                    : "Select category first"
              }
              options={levelOptions}
              value={selectedLevelValue}
              labelClassName="mb-1 text-sm text-[#374151]"
              controlWrapperClassName="mt-0"
              selectClassName="h-[42px] capitalize rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm text-[#000] sm:text-base"
              optionClassName="capitalize"
              icon={ChevronDown}
              iconProps={{ size: 20, className: "text-[#000]" }}
              dropdownClassName="max-h-[45vh] w-full overflow-y-auto rounded-lg"
              disabled={!selectedCategoryValue}
              onChange={(value) => {
                setSelectedLevelValue(value);
                if (typeof onChange === "function") {
                  onChange({ courseLevel: value });
                }
              }}
            />
          </div>

          <div className="mt-5 sm:mt-7">
            <label className="mb-1 block text-sm  text-[#374151]">
              Course Type
            </label>
            <input
              value={courseData?.courseEnrollementType || ""}
              readOnly
              placeholder="type"
              className="h-[42px] w-full rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm capitalize text-[#000] outline-none sm:text-base"
            />
          </div>

          <div className="mt-5 sm:mt-7">
            <FormSelect
              label="Age Group"
              placeholder={
                isCategoryDetailsLoading
                  ? "Loading..."
                  : selectedCategoryValue
                    ? "Select Age Group"
                    : "Select category first"
              }
              options={ageGroupOptions}
              value={selectedAgeGroupValue}
              labelClassName="mb-1 text-sm text-[#374151]"
              controlWrapperClassName="mt-0"
              selectClassName="h-[42px] capitalize rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm text-[#000] sm:text-base"
              optionClassName="capitalize"
              icon={ChevronDown}
              iconProps={{ size: 20, className: "text-[#000]" }}
              dropdownClassName="max-h-[45vh] w-full overflow-y-auto rounded-lg"
              disabled={!selectedCategoryValue}
              onChange={(value) => {
                setSelectedAgeGroupValue(value);
                if (typeof onChange === "function") {
                  onChange({ courseAgeGroup: value });
                }
              }}
            />
          </div>

          <div>
            <label className="mb-1 mt-5 sm:mt-6 block text-sm   text-[#374151]">
              Price <span className="text-[#EF4444]">*</span>
            </label>
            <div className="flex h-[42px] w-full items-center rounded-lg border border-[#D1D5DB] bg-white px-3">
              <span className="mr-1.5 text-sm text-[#6B7280] sm:text-base">
                Rs
              </span>
              <input
                type="number"
                min="0"
                step="0.5"
                value={liveClassPrice}
                onChange={(event) => {
                  setLiveClassPrice(event.target.value);
                  if (typeof onChange === "function") {
                    onChange({ coursePrice: event.target.value });
                  }
                }}
                placeholder="0.00"
                className="h-full w-full bg-transparent text-sm text-[#000] outline-none placeholder:text-[#9CA3AF] [appearance:textfield] sm:text-base [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BasicInformationSection;
